import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import { canAccess, ROLE_LABELS, ROLE_COLORS, isReadOnly } from '../../utils/roles';

const navSections = [
    {
        title: 'OPD', icon: '🏥', color: '#60a5fa', module: 'opd',
        items: [
            { label: 'New Registration', path: '/opd/register' },
            { label: 'Appointments', path: '/opd/appointments' },
            { label: 'Doctor View', path: '/opd/doctor-view' },
            { label: 'Patient Search', path: '/patients/search' },
            { label: 'OPD Billing', path: '/opd/billing' },
            { label: 'OPD Reports', path: '/opd/reports' },
        ]
    },
    {
        title: 'IPD', icon: '🛏️', color: '#34d399', module: 'ipd',
        items: [
            { label: 'IPD Registration', path: '/ipd/register' },
            { label: 'IPD Billing', path: '/ipd/billing' },
            { label: 'Ward Inventory Request', path: '/ipd/ward-request' },
            { label: 'Billing Report', path: '/ipd/billing-report' },
            { label: 'IPD Patients Report', path: '/ipd/patients-report' },
            { label: 'Patient History', path: '/patients/history' },
            { label: 'Ward Management', path: '/ipd/wards' },
        ]
    },
    {
        title: 'Emergency (A&E)', icon: '🚨', color: '#f87171', module: 'emergency',
        items: [
            { label: 'ER Live Board', path: '/ae' },
            { label: 'A&E Reports / MLC', path: '/ae/reports' },
        ]
    },
    {
        title: 'Radiology', icon: 'CARM', color: '#38bdf8', module: 'radiology',
        items: [
            { label: 'Dashboard', path: '/pacs/dashboard' },
            { label: 'Register', path: '/pacs/register' },
            { label: 'PACS Worklist', path: '/pacs/worklist' },
            { label: 'Reporting', path: '/pacs/reporting' },
            { label: 'Report Approval', path: '/pacs/approval' },
            { label: 'Billing', path: '/pacs/billing' },
        ]
    },
    {
        title: 'Laboratory', icon: '🔬', color: '#c084fc', module: 'lab',
        items: [
            { label: 'Lab Order', path: '/lab/order' },
            { label: 'Sample Collection', path: '/lab/sample-collection' },
            { label: 'Sample Tracking', path: '/lab/sample-tracking' },
            { label: 'Lab Worklist', path: '/lab/worklist' },
            { label: 'Parameter Settings', path: '/lab/parameters' },
            { label: 'Lab Inventory', path: '/lab/inventory' },
            { label: 'QC Log', path: '/lab/qc' },
            { label: 'Lab Dashboard', path: '/lab/dashboard' },
        ]
    },
    {
        title: 'Psychiatry', icon: '🧠', color: '#a78bfa', module: 'psychiatry',
        items: [
            { label: 'Psychiatry', path: '/psych' },
        ]
    },
    {
        title: 'Physiotherapy', icon: '🦽', color: '#0369a1', module: 'physio',
        items: [
            { label: 'Physiotherapy', path: '/physio' },
        ]
    },
    {
        title: 'ICU / CCU', icon: '🩺', color: '#dc2626', module: 'icu',
        items: [
            { label: 'ICU / CCU', path: '/icu' },
        ]
    },
    {
        title: 'Pharmacy', icon: '💊', color: '#059669', module: 'pharmacy',
        items: [
            { label: 'Pharmacy', path: '/pharmacy' },
            { label: 'Dispense', path: '/pharmacy/dispense' },
            { label: 'Dispense History', path: '/pharmacy/history' },
        ]
    },
    {
        title: 'Blood Bank', icon: '🩸', color: '#dc2626', module: 'blood_bank',
        items: [
            { label: 'Blood Bank', path: '/blood-bank' },
            { label: 'Blood Donors', path: '/blood-bank/donors' },
            { label: 'Issue Blood', path: '/blood-bank/issue' },
        ]
    },
    {
        title: 'Queue', icon: '🎫', color: '#f59e0b', module: 'queue',
        items: [
            { label: 'Queue Management', path: '/queue/manage' },
            { label: 'Queue Display', path: '/queue/display' },
        ]
    },
    {
        title: 'Patients', icon: '👤', color: '#86efac', module: 'patients',
        items: [
            { label: 'Register Patient', path: '/patients/register' },
            { label: 'Patient Search', path: '/patients/search' },
        ]
    },
    {
        title: 'Management', icon: '📈', color: '#f59e0b', module: 'reports',
        items: [
            { label: 'Reports', path: '/reports' },
            { label: 'User Management', path: '/admin/users' },
        ]
    },
];

const DEMO_MODULES = ['opd', 'ipd', 'lab', 'pharmacy'];

export default function Layout() {
    const { user, logout } = useAuthStore();
    const navigate = useNavigate();
    const [collapsed, setCollapsed] = useState(false);
    const [openSections, setOpenSections] = useState({});
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [demoToast, setDemoToast] = useState(false);

    useEffect(() => {
        const handler = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (!mobile) setMobileOpen(false);
        };
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);

    const handleLogout = () => { logout(); navigate('/login'); };
    const toggleSection = (title) => setOpenSections(p => ({ ...p, [title]: !p[title] }));
    const closeMobileSidebar = () => { if (isMobile) setMobileOpen(false); };
    const showDemoToast = () => {
        setDemoToast(true);
        setTimeout(() => setDemoToast(false), 3000);
    };;

    const roleColor = user?.role ? (ROLE_COLORS[user.role] || { bg: '#1e3a5f', text: '#fff' }) : { bg: '#1e3a5f', text: '#fff' };
    const roleLabel = user?.role ? (ROLE_LABELS[user.role] || user.role) : 'Staff';

    const sidebarVisible = isMobile ? mobileOpen : true;
    const sidebarWidth = isMobile ? 260 : (collapsed ? 64 : 230);

    return (
        <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', fontFamily: "'Segoe UI', sans-serif" }}>
            {demoToast && (
                <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#1e293b', color: '#fff', padding: '10px 20px', borderRadius: 10, fontSize: 13, fontWeight: 600, zIndex: 9999, boxShadow: '0 4px 20px rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    🔒 Not available in demo. Contact admin to enable.
                </div>
            )}
            {/* Mobile overlay backdrop */}
            {isMobile && mobileOpen && (
                <div onClick={() => setMobileOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 99 }} />
            )}
            <div style={{
                width: sidebarWidth,
                background: 'linear-gradient(180deg, #0f2942 0%, #1e3a5f 100%)',
                color: 'white', display: 'flex', flexDirection: 'column',
                transition: 'transform 0.25s, width 0.2s', flexShrink: 0,
                boxShadow: '4px 0 20px rgba(0,0,0,0.2)',
                position: isMobile ? 'fixed' : 'relative',
                top: 0, left: 0, height: '100vh', zIndex: 100,
                transform: isMobile ? (mobileOpen ? 'translateX(0)' : 'translateX(-100%)') : 'none',
            }}>
                <div style={{ padding: (collapsed && !isMobile) ? '18px 0' : '18px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: (collapsed && !isMobile) ? 'center' : 'flex-start', gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #0f766e, #0d9488)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>🏥</div>
                    {(!collapsed || isMobile) && <div><div style={{ fontWeight: 800, fontSize: 16, letterSpacing: 1 }}>HIMS</div><div style={{ fontSize: 9, color: '#5eead4', letterSpacing: 2 }}>Hospital Information System</div></div>}
                </div>

                <nav style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
                    {/* Dashboard link */}
                    <NavLink to="/" end onClick={closeMobileSidebar} style={({ isActive }) => ({ display: 'flex', alignItems: 'center', gap: 10, padding: (collapsed && !isMobile) ? '10px 0' : '10px 14px', justifyContent: (collapsed && !isMobile) ? 'center' : 'flex-start', textDecoration: 'none', marginBottom: 2, color: isActive ? 'white' : '#93c5fd', background: isActive ? 'rgba(255,255,255,0.12)' : 'transparent', borderLeft: isActive ? '3px solid #60a5fa' : '3px solid transparent', fontSize: 13, fontWeight: isActive ? 700 : 400, borderRadius: '0 8px 8px 0', marginRight: 8, transition: 'all 0.15s' })}>
                        <span style={{ fontSize: 17 }}>📊</span>
                        {(!collapsed || isMobile) && <span>Dashboard</span>}
                    </NavLink>

                    {/* Collapsible department sections */}
                    {navSections.map(section => {
                        const isDemo = user?.is_demo;
                        const isDemoLocked = isDemo && !DEMO_MODULES.includes(section.module);
                        if (!isDemo && !canAccess(user?.role, section.module)) return null;
                        const isOpen = openSections[section.title];
                        return (
                            <div key={section.title}>
                                {/* Section header */}
                                <div
                                    onClick={() => isDemoLocked ? showDemoToast() : toggleSection(section.title)}
                                    style={{
                                        display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between',
                                        padding: collapsed ? '9px 0' : '9px 14px', cursor: 'pointer',
                                        color: isOpen && !isDemoLocked ? 'white' : '#93c5fd',
                                        background: isOpen && !isDemoLocked ? 'rgba(255,255,255,0.07)' : 'transparent',
                                        borderLeft: isOpen && !isDemoLocked ? `3px solid ${section.color}` : '3px solid transparent',
                                        marginRight: 8, borderRadius: '0 8px 8px 0',
                                        transition: 'all 0.15s', userSelect: 'none',
                                        opacity: 1,
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ fontSize: 16, flexShrink: 0 }}>
                                            {section.icon === 'CARM' ? (
                                                <svg width="18" height="18" viewBox="0 0 46 42" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M 32 30 C 32 30 44 30 44 18 C 44 6 32 6 32 6" stroke="currentColor" strokeWidth="5" fill="none" strokeLinecap="round"/>
                                                    <rect x="26" y="2" width="10" height="6" rx="2" fill="currentColor"/>
                                                    <rect x="24" y="31" width="12" height="5" rx="2" fill="currentColor"/>
                                                    <rect x="18" y="22" width="12" height="12" rx="2" fill="currentColor" opacity="0.8"/>
                                                    <circle cx="21" cy="37" r="2.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                                    <circle cx="27" cy="37" r="2.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                                    <rect x="2" y="6" width="10" height="8" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                                    <rect x="8" y="14" width="2" height="10" rx="1" fill="currentColor" opacity="0.8"/>
                                                </svg>
                                            ) : section.icon}
                                        </span>
                                        {(!collapsed || isMobile) && <span style={{ fontSize: 13, fontWeight: isOpen ? 700 : 400 }}>{section.title}</span>}
                                        {(!collapsed || isMobile) && isDemoLocked && <span style={{ fontSize: 10, opacity: 0.6 }}>🔒</span>}
                                    </div>
                                    {(!collapsed || isMobile) && !isDemoLocked && <span style={{ fontSize: 10, color: '#64748b' }}>{isOpen ? '▾' : '▸'}</span>}
                                </div>

                                {/* Sub-links */}
                                {isOpen && !isDemoLocked && (!collapsed || isMobile) && section.items.map(item => (
                                    <NavLink key={item.path} to={item.path} onClick={closeMobileSidebar}
                                        style={({ isActive }) => ({
                                            display: 'flex', alignItems: 'center', gap: 8,
                                            padding: '7px 14px 7px 38px',
                                            textDecoration: 'none', fontSize: 12,
                                            color: isActive ? 'white' : '#7dd3fc',
                                            background: isActive ? 'rgba(255,255,255,0.1)' : 'transparent',
                                            borderLeft: isActive ? `3px solid ${section.color}` : '3px solid transparent',
                                            marginRight: 8, borderRadius: '0 6px 6px 0',
                                            fontWeight: isActive ? 600 : 400,
                                            transition: 'all 0.1s',
                                        })}>
                                        {item.label}
                                    </NavLink>
                                ))}
                            </div>
                        );
                    })}
                </nav>

                <div style={{ padding: (collapsed && !isMobile) ? '12px 0' : '14px 14px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                    {(!collapsed || isMobile) && user && (
                        <div style={{ marginBottom: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                                <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg, #0f766e, #1e3a5f)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, flexShrink: 0, color: 'white' }}>
                                    {user.full_name?.[0] || 'U'}
                                </div>
                                <div>
                                    <div style={{ fontSize: 12, fontWeight: 600 }}>{user.full_name}</div>
                                    <div style={{ fontSize: 10, color: '#94a3b8' }}>{user.designation || user.department || ''}</div>
                                </div>
                            </div>
                            <div style={{ background: roleColor.bg, color: roleColor.text, borderRadius: 6, padding: '3px 8px', fontSize: 10, fontWeight: 700, textAlign: 'center', letterSpacing: 0.5 }}>
                                {roleLabel}
                            </div>
                        </div>
                    )}
                    <button onClick={handleLogout} style={{ width: '100%', padding: '8px', background: 'rgba(220,38,38,0.15)', color: '#fca5a5', border: '1px solid rgba(220,38,38,0.3)', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                        {(collapsed && !isMobile) ? '🚪' : '🚪 Logout'}
                    </button>
                </div>
            </div>

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f8fafc', minWidth: 0 }}>
                <div style={{ background: 'white', padding: '10px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', flexShrink: 0 }}>
                    <button
                        onClick={() => isMobile ? setMobileOpen(!mobileOpen) : setCollapsed(!collapsed)}
                        style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', fontSize: 18, padding: '6px 10px', color: '#374151', flexShrink: 0 }}>
                        {isMobile ? '☰' : (collapsed ? '▶' : '◀')}
                    </button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 20, overflow: 'hidden' }}>
                        {!isMobile && (
                            <>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
                                    <span style={{ fontSize: 12, color: '#64748b' }}>System Online</span>
                                </div>
                                <div style={{ fontSize: 12, color: '#64748b' }}>
                                    {new Date().toLocaleDateString('en-PK', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                                </div>
                            </>
                        )}
                        {user && (
                            <div style={{ background: roleColor.bg, color: roleColor.text, borderRadius: 20, padding: '4px 10px', fontSize: isMobile ? 10 : 11, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: isMobile ? 160 : 'none' }}>
                                {isMobile ? user.full_name : `${user.full_name} — ${roleLabel}`}
                            </div>
                        )}
                    </div>
                </div>
                {isReadOnly(user?.role) && (
                    <div style={{ background: '#fef3c7', borderBottom: '2px solid #f59e0b', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        <span style={{ fontSize: 16 }}>👁️</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>View Only Mode</span>
                        <span style={{ fontSize: 12, color: '#78350f' }}>— You have read-only access. No changes can be saved.</span>
                    </div>
                )}
                <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? 12 : 24 }}>
                    <Outlet />
                </div>
            </div>
        </div>
    );
}
